// Scam-KG console.
//
//   ScamKgView   /chatbot/scam-kg   grounded retrieval, stats, explorer
//
// The Scam-KG is a typed service reached through the unenveloped passthrough at
// /api/scam-kg/*. The legacy fraud graph that used to share this file has been
// removed along with its screen, because no scope document asks for it and the
// Scam-KG supersedes it.
//
// A correction, because the first version of this note gave a reason that was
// wrong twice and a wrong reason recorded as evidence is worse than no note. That
// store is NOT Postgres: backend/app/api/fraudgraph.py documents a MemoryGraphStore
// holding nodes and edges in Python lists. And it is NOT writerless: it has five
// write endpoints (node curate, retrieve-template, revalidation refresh, duplicate
// decision, feed import) plus a scheduled revalidation worker. What is true, and is
// the actual reason, is that the `fraud_graph_*` POSTGRES TABLES have no writer, and
// that the console screen is not a scope requirement. The backend surface is a
// separate decision and still stands.
//
// Rendering rules that are not negotiable:
//
//   * Node colour comes from NODE_DOMAIN_COLOR, and every node ALSO carries a
//     shape and a text label for the same domain. Two operators looking at the
//     same canvas must be able to name a node's family without separating hues.
//   * The Vue build listed 3d-force-graph and three as dependencies. Nothing
//     imported them. Only the 2D canvas is ported.
//   * force-graph mutates a link's `source`/`target` from an id into the node
//     object after the first tick, so every read of an endpoint goes through
//     endId().

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import ForceGraph2D, {
  type ForceGraphMethods,
  type LinkObject,
  type NodeObject,
} from "react-force-graph-2d";

import { ApiError, http, org, qs } from "./api";
import { useSession } from "./App";
import { BarChartCard, StatTile } from "./charts";
import {
  BRAND,
  CHROME,
  INK,
  NODE_DOMAIN_COLOR,
  SURFACE,
  readMode,
  seriesColor,
  type Mode,
} from "./theme";
import {
  Badge,
  Banner,
  Drawer,
  Empty,
  Field,
  Num,
  Spinner,
  Table,
  Tabs,
  useAsync,
  useToast,
} from "./ui";

// ============================================================ small helpers

/** Light/dark, kept in sync with the shell's data-theme attribute. */
function useMode(): Mode {
  const [mode, setMode] = useState<Mode>(readMode);
  useEffect(() => {
    const sync = () => setMode(readMode());
    const obs = new MutationObserver(sync);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    const mq = window.matchMedia?.("(prefers-color-scheme: light)");
    mq?.addEventListener("change", sync);
    return () => {
      obs.disconnect();
      mq?.removeEventListener("change", sync);
    };
  }, []);
  return mode;
}

function errMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message || fallback;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/** Screen-level error state. The trace id is the only handle support has. */
function ErrorBanner({ error, what }: { error: unknown; what: string }) {
  if (!error) return null;
  const api = error instanceof ApiError ? error : null;
  return (
    <Banner tone="critical" traceId={api?.traceId}>
      {what}: {errMessage(error, "request failed")}
    </Banner>
  );
}

function asNumber(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

function titleCase(value: string): string {
  return String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

// ============================================================ domains + shapes

const DOMAINS = [
  "scam",
  "flow",
  "contact",
  "portal",
  "organisation",
  "playbook",
  "evidence",
  "unknown",
] as const;
type DomainKey = (typeof DOMAINS)[number];

const domainColor = (d: DomainKey): string => NODE_DOMAIN_COLOR[d] ?? NODE_DOMAIN_COLOR.unknown;

type Shape = "circle" | "triangle" | "diamond" | "square" | "pentagon" | "hexagon" | "cross" | "star";

// Shape is redundant with colour on purpose. It is the channel that survives a
// colour-vision deficiency, a greyscale print, and a projector.
const DOMAIN_SHAPE: Record<DomainKey, Shape> = {
  scam: "triangle",
  flow: "diamond",
  contact: "circle",
  portal: "square",
  organisation: "hexagon",
  playbook: "pentagon",
  evidence: "cross",
  unknown: "star",
};

const DOMAIN_LABEL: Record<DomainKey, string> = {
  scam: "Scam / signal",
  flow: "Flow / cluster",
  contact: "Contact / identity",
  portal: "Portal / domain",
  organisation: "Organisation",
  playbook: "Playbook",
  evidence: "Evidence / source",
  unknown: "Other",
};

function regular(sides: number, r: number, rotation: number): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (let i = 0; i < sides; i++) {
    const a = rotation + (i * 2 * Math.PI) / sides;
    out.push([r * Math.cos(a), r * Math.sin(a)]);
  }
  return out;
}

function alternating(points: number, outer: number, inner: number, rotation: number): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = rotation + (i * Math.PI) / points;
    out.push([r * Math.cos(a), r * Math.sin(a)]);
  }
  return out;
}

function plus(arm: number, half: number): Array<[number, number]> {
  return [
    [-half, -arm],
    [half, -arm],
    [half, -half],
    [arm, -half],
    [arm, half],
    [half, half],
    [half, arm],
    [-half, arm],
    [-half, half],
    [-arm, half],
    [-arm, -half],
    [-half, -half],
  ];
}

/** Outline for a shape at radius r, centred on the origin. Empty means "a circle". */
function outline(shape: Shape, r: number): Array<[number, number]> {
  switch (shape) {
    case "triangle":
      return regular(3, r * 1.25, -Math.PI / 2);
    case "diamond":
      return regular(4, r * 1.3, -Math.PI / 2);
    case "square":
      return regular(4, r * 1.1, Math.PI / 4);
    case "pentagon":
      return regular(5, r * 1.2, -Math.PI / 2);
    case "hexagon":
      return regular(6, r * 1.12, 0);
    case "star":
      return alternating(5, r * 1.4, r * 0.62, -Math.PI / 2);
    case "cross":
      return plus(r * 1.25, r * 0.44);
    case "circle":
    default:
      return [];
  }
}

function traceShape(ctx: CanvasRenderingContext2D, shape: Shape, x: number, y: number, r: number): void {
  const pts = outline(shape, r);
  ctx.beginPath();
  if (pts.length === 0) {
    ctx.arc(x, y, r, 0, 2 * Math.PI);
    return;
  }
  pts.forEach(([dx, dy], i) => {
    if (i === 0) ctx.moveTo(x + dx, y + dy);
    else ctx.lineTo(x + dx, y + dy);
  });
  ctx.closePath();
}

/** The legend glyph: the same outline the canvas draws, at swatch size. */
function ShapeSwatch({ domain, color, title }: { domain: DomainKey; color?: string; title?: string }) {
  const shape = DOMAIN_SHAPE[domain];
  const fill = color ?? domainColor(domain);
  const pts = outline(shape, 4.2);
  return (
    <svg className="swatch" viewBox="-6 -6 12 12" role="img" aria-label={title ?? DOMAIN_LABEL[domain]}>
      {title && <title>{title}</title>}
      {pts.length === 0 ? (
        <circle cx={0} cy={0} r={4.2} fill={fill} />
      ) : (
        <polygon points={pts.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(" ")} fill={fill} />
      )}
    </svg>
  );
}

function DomainLegend({ shown }: { shown: DomainKey[] }) {
  return (
    <div className="legend">
      {shown.map((d) => (
        <span className="item" key={d}>
          <ShapeSwatch domain={d} />
          {DOMAIN_LABEL[d]}
        </span>
      ))}
    </div>
  );
}

// ============================================================ graph canvas

export interface VizNode {
  id: string;
  /** Text drawn under the node and shown in the detail panel. */
  label: string;
  /** The exact type, spelled out. Colour and shape only carry the family. */
  typeLabel: string;
  domain: DomainKey;
  radius: number;
  /** Hubs and scam types keep their label at every zoom level. */
  pinLabel: boolean;
  /** Overrides the domain colour when a "colour by" axis is active. */
  tint?: string;
}

export interface VizLink {
  id: string;
  source: string;
  target: string;
  rel: string;
}

type FNode = NodeObject<VizNode>;
type FLink = LinkObject<VizNode, VizLink>;
type FGRef = ForceGraphMethods<FNode, FLink>;

export interface GraphHandle {
  fit(): void;
  centerNode(id: string): void;
}

interface GraphCanvasProps {
  nodes: VizNode[];
  links: VizLink[];
  selectedId: string | null;
  height: number;
  onNodeClick(id: string): void;
  onNodeRightClick?(id: string): void;
  onBackgroundClick(): void;
  children?: ReactNode;
}

function endId(v: FLink["source"]): string {
  if (v && typeof v === "object") return String((v as { id?: string | number }).id ?? "");
  return String(v ?? "");
}

const GraphCanvas = forwardRef<GraphHandle, GraphCanvasProps>(function GraphCanvas(
  { nodes, links, selectedId, height, onNodeClick, onNodeRightClick, onBackgroundClick, children },
  handleRef
) {
  const mode = useMode();
  const wrap = useRef<HTMLDivElement>(null);
  const fg = useRef<FGRef | undefined>(undefined);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [hoverId, setHoverId] = useState<string | null>(null);

  // The working set is kept across data updates so that expanding a node adds to
  // the layout instead of reshuffling it: force-graph stores x/y/vx/vy ON the
  // node object, and the object for an id that is already on screen survives.
  const store = useRef(new Map<string, FNode>()).current;

  const data = useMemo(() => {
    const wanted = new Set(nodes.map((n) => n.id));
    for (const n of nodes) {
      const prev = store.get(n.id);
      if (prev) Object.assign(prev, n);
      else store.set(n.id, { ...n });
    }
    for (const id of Array.from(store.keys())) if (!wanted.has(id)) store.delete(id);

    const seen = new Set<string>();
    const out: FLink[] = [];
    for (const e of links) {
      if (!wanted.has(e.source) || !wanted.has(e.target)) continue;
      const key = `${e.source}|${e.rel}|${e.target}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ id: e.id, source: e.source, target: e.target, rel: e.rel });
    }
    return { nodes: Array.from(store.values()), links: out };
  }, [nodes, links, store]);

  const activeId = hoverId ?? selectedId;

  const neighbourhood = useMemo(() => {
    const s = new Set<string>();
    if (!activeId) return s;
    for (const l of data.links) {
      const a = endId(l.source);
      const b = endId(l.target);
      if (a === activeId) s.add(b);
      if (b === activeId) s.add(a);
    }
    return s;
  }, [activeId, data.links]);

  useImperativeHandle(
    handleRef,
    () => ({
      fit: () => fg.current?.zoomToFit(500, 60),
      centerNode: (id: string) => {
        const n = store.get(id);
        if (n && n.x != null && n.y != null) {
          fg.current?.centerAt(n.x, n.y, 500);
          fg.current?.zoom(2.4, 500);
        }
      },
    }),
    [store]
  );

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Spacing tuned so clusters separate instead of piling into one blob. The
  // forces only exist once the engine has built them, hence the delay.
  useEffect(() => {
    const t = window.setTimeout(() => {
      const charge = fg.current?.d3Force("charge");
      if (charge && typeof charge.strength === "function") charge.strength(-180);
      const link = fg.current?.d3Force("link");
      if (link && typeof link.distance === "function") link.distance(58);
    }, 120);
    return () => window.clearTimeout(t);
  }, []);

  // Frame the graph the first time nodes arrive, and again whenever it goes from
  // empty back to populated (a reload, a filter change).
  const wasEmpty = useRef(true);
  useEffect(() => {
    if (data.nodes.length === 0) {
      wasEmpty.current = true;
      return;
    }
    if (!wasEmpty.current) return;
    wasEmpty.current = false;
    const t = window.setTimeout(() => fg.current?.zoomToFit(500, 60), 650);
    return () => window.clearTimeout(t);
  }, [data.nodes.length]);

  const idle = CHROME[mode].axis;
  const ink = INK[mode].secondary;
  const halo = SURFACE[mode];

  const incident = (l: FLink): boolean =>
    activeId != null && (endId(l.source) === activeId || endId(l.target) === activeId);

  return (
    <div className="graph-canvas" style={{ height }}>
      <div ref={wrap} style={{ position: "absolute", inset: 0 }}>
        {size.w > 0 && (
          <ForceGraph2D
            ref={fg}
            width={size.w}
            height={size.h}
            graphData={data}
            backgroundColor={SURFACE[mode]}
            cooldownTicks={110}
            nodeRelSize={4}
            linkColor={(l: FLink) => (incident(l) ? BRAND.gold : idle)}
            linkWidth={(l: FLink) => (incident(l) ? 1.8 : 0.6)}
            linkDirectionalParticles={(l: FLink) => (incident(l) ? 3 : 0)}
            linkDirectionalParticleWidth={2}
            linkDirectionalArrowLength={3}
            linkDirectionalArrowRelPos={1}
            onNodeHover={(n: FNode | null) => setHoverId(n ? String(n.id ?? "") : null)}
            onNodeClick={(n: FNode) => {
              onNodeClick(String(n.id ?? ""));
              if (n.x != null && n.y != null) fg.current?.centerAt(n.x, n.y, 400);
            }}
            onNodeRightClick={(n: FNode) => onNodeRightClick?.(String(n.id ?? ""))}
            onBackgroundClick={onBackgroundClick}
            nodePointerAreaPaint={(n: FNode, paint: string, ctx: CanvasRenderingContext2D) => {
              // Hit testing has to follow the drawn outline, or a triangle is
              // clickable where nothing is painted.
              ctx.fillStyle = paint;
              traceShape(ctx, DOMAIN_SHAPE[n.domain], n.x ?? 0, n.y ?? 0, n.radius + 1);
              ctx.fill();
            }}
            nodeCanvasObject={(n: FNode, ctx: CanvasRenderingContext2D, scale: number) => {
              const id = String(n.id ?? "");
              const dim = activeId != null && id !== activeId && !neighbourhood.has(id);
              ctx.globalAlpha = dim ? 0.18 : 1;

              traceShape(ctx, DOMAIN_SHAPE[n.domain], n.x ?? 0, n.y ?? 0, n.radius);
              ctx.fillStyle = n.tint ?? domainColor(n.domain);
              ctx.fill();
              ctx.lineWidth = (id === activeId ? 2.2 : 1) / scale;
              ctx.strokeStyle = id === activeId ? BRAND.gold : halo;
              ctx.stroke();

              const show = id === activeId || n.pinLabel || scale > 2.2 || (neighbourhood.has(id) && scale > 1.4);
              if (show) {
                ctx.font = `${n.pinLabel ? 700 : 500} ${Math.max(3, 9 / scale)}px ${"sans-serif"}`;
                ctx.fillStyle = ink;
                ctx.textAlign = "center";
                ctx.fillText(String(n.label || id).slice(0, 24), n.x ?? 0, (n.y ?? 0) + n.radius + 8 / scale);
              }
              ctx.globalAlpha = 1;
            }}
          />
        )}
      </div>
      {children}
    </div>
  );
});

// ============================================================ Scam-KG types

interface ScamKgStats {
  organization_id?: string;
  node_count?: number;
  counts_by_type?: Record<string, number>;
  queue_pending?: number;
}

interface ScamKgVerdict {
  entity_id?: string;
  role?: string;
  label?: string;
  band?: string;
  Lambda?: number;
  A?: number;
  B?: number;
  n_evidence?: number;
}

interface ScamKgEvidence {
  evidence_type?: string;
  discriminator?: string;
  llr?: number;
}

interface ScamKgPlaybook {
  mechanism?: string;
  urgency_tier?: string;
  steps?: string[];
  official_rails?: { helpline?: string; portal?: string };
}

interface ScamKgProvenance {
  source_id?: string;
  raw_key?: string;
}

interface ScamKgRouting {
  mode?: string;
  need_dense?: boolean;
}

interface ScamKgRetrieve {
  organization_id?: string;
  verdict?: ScamKgVerdict;
  evidence_trail?: ScamKgEvidence[];
  provenance?: ScamKgProvenance[];
  routing?: ScamKgRouting;
  playbook?: ScamKgPlaybook;
  backend?: string;
}

interface KgNode {
  id: string;
  node_type: string;
  label: string;
  layer?: number;
  current_label?: string | null;
  risk_score?: number | null;
  review_status?: string | null;
  trust_weight?: number | null;
  dataset_label?: string | null;
  cluster_id?: string | null;
  strata?: string | null;
  scam_type?: string | null;
  language?: string | null;
  user_strata?: string | null;
  user_cluster?: string | null;
  mirror_role?: string | null;
  emotional_state?: string | null;
}

interface KgEdge {
  from_node_id?: string;
  to_node_id?: string;
  type?: string;
}

interface KgOverview {
  nodes?: KgNode[];
  edges?: KgEdge[];
}

interface KgNeighbors {
  neighbors?: KgNode[];
  edges?: KgEdge[];
}

interface KgNodeDetail {
  properties?: Record<string, unknown>;
  sparse_space?: { terms?: string[]; count?: number };
  shallow_dense_space?: { dim?: number; vector_preview?: number[] };
  deep_dense_space?: { stored?: boolean };
  detector?: string;
  routing_mode?: string;
  top_hub?: string;
  llr_contribution?: number;
  event_time?: string;
  record_time?: string;
}

interface KgNodeRaw {
  has_raw?: boolean;
  reason?: string;
  payload_json?: unknown;
  payload_text?: string;
  size_bytes?: number;
  content_type?: string;
  truncated?: boolean;
}

// ---- humanisation ----------------------------------------------------------

const L1_TYPES = new Set([
  "RawMessage",
  "RawTransaction",
  "RawIdentity",
  "ReferenceDocument",
  "SourceRecord",
]);

// Every Scam-KG node type folded onto a graph domain. The colour and the shape
// carry the domain; the human type name is always printed beside it.
const KG_TYPE_DOMAIN: Record<string, DomainKey> = {
  ScamType: "scam",
  RiskSignal: "scam",
  ManipulationTactic: "scam",
  Cluster: "flow",
  Entity: "organisation",
  Playbook: "playbook",
  MirrorTemplate: "playbook",
  ResponseStyle: "playbook",
  UserProfile: "contact",
  UserStrata: "contact",
  UserCluster: "contact",
  EmotionalState: "contact",
  Evidence: "evidence",
};

function kgDomain(nodeType: string): DomainKey {
  if (L1_TYPES.has(nodeType)) return "evidence";
  const mapped = KG_TYPE_DOMAIN[nodeType];
  if (mapped) return mapped;
  if (nodeType.endsWith("Facet") || nodeType.endsWith("Hub")) return "flow";
  return "unknown";
}

function kgSize(nodeType: string): number {
  if (nodeType === "ScamType" || nodeType.endsWith("Hub")) return 6;
  if (["Cluster", "Playbook", "UserStrata", "UserCluster"].includes(nodeType)) return 5;
  if (
    ["Entity", "RiskSignal", "EmotionalState", "ManipulationTactic", "MirrorTemplate", "ResponseStyle"].includes(
      nodeType
    )
  ) {
    return 4;
  }
  return 2.5;
}

const KG_TYPE_NAMES: Record<string, string> = {
  RawMessage: "Message",
  RawTransaction: "Transaction",
  RawIdentity: "Identity",
  ReferenceDocument: "Reference document",
  SourceRecord: "Source record",
  Entity: "Entity",
  RiskSignal: "Risk signal",
  ScamType: "Scam pattern",
  Cluster: "Scam cluster",
  Evidence: "Evidence",
  Playbook: "Response playbook",
  UserStrata: "User strata",
  UserCluster: "User cluster",
  UserProfile: "User",
  EmotionalState: "Emotional state",
  ManipulationTactic: "Manipulation tactic",
  MirrorTemplate: "Mirror template",
  ResponseStyle: "Response style",
};

const KG_TYPE_DESCRIPTIONS: Record<string, string> = {
  RawMessage: "An actual message that was ingested — real content, kept immutable.",
  RawTransaction: "A transaction record that was ingested.",
  RawIdentity: "An identity (sender / number / handle) seen in the data.",
  ReferenceDocument: "A reference document (advisory / SOP source).",
  SourceRecord: "A record of where this data came from.",
  Entity: "A real-world entity — brand, UPI ID, phone, domain or person — extracted from messages.",
  RiskSignal: "A fraud red flag detected in messages (for example 'asks for OTP').",
  ScamType: "A scam pattern — a recurring combination of tactics.",
  Cluster: "A group of similar messages the system grouped together.",
  Evidence: "A piece of evidence connecting a message to a verdict — a detector's finding.",
  Playbook: "The recommended response / SOP — for example report to 1930, cybercrime.gov.in.",
  UserStrata: "A user socioeconomic segment — who a scam targets and how to mirror them.",
  UserCluster: "A user contextual-exposure cohort — better for outreach than for scoring.",
  UserProfile: "A live conversation's resolved segment, awareness, language and emotional state.",
  EmotionalState: "An emotional state the assistant mirrors (panic, shame, anger).",
  ManipulationTactic: "A psychological lever scammers pull (fear of authority, urgency, greed).",
  MirrorTemplate: "A reviewer-approved empathy template used to mirror the user.",
  ResponseStyle: "A rendering style (respectful, simple-directive, reassuring) a segment prefers.",
};

const KG_TERM_LABELS: Record<string, string> = {
  asks_for_otp_or_pin: "Asks for OTP / PIN",
  qr_scan_to_receive_money: "QR scan to 'receive' money",
  upi_qr: "UPI QR code",
  upi_vpa: "UPI ID (VPA)",
  authority_impersonation: "Impersonates an authority",
  urgency_pressure: "Urgency / pressure",
  payment_requested: "Payment requested",
  remote_control_app: "Remote-control app",
  secrecy_demand: "Demands secrecy",
  lookalike_domain: "Look-alike domain",
  shortened_link: "Shortened link",
  account_block: "Account-block threat",
};

const KG_DETECTOR_NAMES: Record<string, string> = {
  router: "Routing evidence",
  slu_deterministic: "Signal evidence",
  dataset: "Dataset-label evidence",
};

function humanTerm(term: string): string {
  if (KG_TERM_LABELS[term]) return KG_TERM_LABELS[term];
  const parts = String(term).split(":");
  return parts.length > 1
    ? `${titleCase(parts[0])}: ${titleCase(parts[parts.length - 1])}`
    : titleCase(term);
}

function humanType(nodeType: string): string {
  if (KG_TYPE_NAMES[nodeType]) return KG_TYPE_NAMES[nodeType];
  if (nodeType.endsWith("Facet")) return `${titleCase(nodeType.replace(/Facet$/, ""))} facet`;
  if (nodeType.endsWith("Hub")) return `${titleCase(nodeType.replace(/Hub$/, ""))} hub`;
  return titleCase(nodeType);
}

function humanName(n: KgNode): string {
  const t = n.node_type;
  const label = (n.label || "").trim();
  if (t === "RawMessage" || t === "ReferenceDocument") return label || humanType(t);
  if (t === "ScamType") return "Scam pattern";
  if (t === "RiskSignal") return humanTerm(label);
  if (t === "Evidence") {
    const detector = (label.split("@")[0] || "").toLowerCase();
    return KG_DETECTOR_NAMES[detector] || (detector ? `Evidence: ${titleCase(detector)}` : "Evidence");
  }
  if (!label) return humanType(t);
  if (label.includes(":")) {
    const [prefix, ...rest] = label.split(":");
    const value = rest.join(":");
    if (prefix === "cluster") return `Cluster ${value.slice(0, 8)}`;
    if (prefix === "facet") return humanTerm(value);
    return `${titleCase(prefix)}: ${titleCase(value)}`;
  }
  if (t === "RawTransaction") return `Transaction ${label}`;
  return label;
}

function verdictText(v: string | null | undefined): string {
  if (!v) return "—";
  return v === "legit_or_unknown" ? "Legit / unknown" : titleCase(v);
}

type ColorBy =
  | "type"
  | "cluster"
  | "strata"
  | "scam-type"
  | "language"
  | "user-strata"
  | "user-cluster"
  | "mirror-role"
  | "emotion";

const COLOR_BY_OPTIONS: Array<{ value: ColorBy; label: string }> = [
  { value: "type", label: "Type (default)" },
  { value: "cluster", label: "Scam cluster" },
  { value: "strata", label: "Scam strata" },
  { value: "scam-type", label: "Scam type" },
  { value: "language", label: "Language" },
  { value: "user-strata", label: "User strata" },
  { value: "user-cluster", label: "User cluster" },
  { value: "mirror-role", label: "Mirroring layer" },
  { value: "emotion", label: "Emotional state" },
];

const AXIS_OF: Record<Exclude<ColorBy, "type">, (n: KgNode) => string | null | undefined> = {
  cluster: (n) => n.cluster_id,
  strata: (n) => n.strata,
  "scam-type": (n) => n.scam_type,
  language: (n) => n.language,
  "user-strata": (n) => n.user_strata,
  "user-cluster": (n) => n.user_cluster,
  "mirror-role": (n) => n.mirror_role,
  emotion: (n) => n.emotional_state,
};

function axisValue(n: KgNode, colorBy: ColorBy): string | null {
  if (colorBy === "type") return null;
  const v = AXIS_OF[colorBy](n);
  return v == null || v === "" ? null : String(v);
}

function axisLabel(value: string, colorBy: ColorBy): string {
  if (colorBy === "language") return value.toUpperCase();
  if (value.includes(":")) return `#${(value.split(":").pop() || value).slice(0, 8)}`;
  if (/^[0-9a-f]{12,}$/i.test(value)) return `#${value.slice(0, 8)}`;
  return titleCase(value);
}

const KG_SEED_KINDS: Array<[string, string]> = [
  ["", "Whole graph (all types)"],
  ["ScamType", "Scam patterns"],
  ["MechanismHub", "Mechanism hubs"],
  ["Cluster", "Clusters"],
  ["Entity", "Entities"],
  ["RiskSignal", "Risk signals"],
  ["Playbook", "Playbooks (SOP)"],
  ["RawMessage", "Raw messages"],
];

const KG_LIMITS: Array<[number, string]> = [
  [500, "500 nodes"],
  [1000, "1,000 nodes"],
  [2000, "2,000 nodes"],
  [5000, "All (up to 5,000)"],
];

const NEIGHBOR_CAP = 40;

// ============================================================ ScamKgView

type KgTab = "explorer" | "retrieve" | "stats";

const KG_TABS = [
  { id: "explorer", label: "Explorer" },
  { id: "retrieve", label: "Retrieve" },
  { id: "stats", label: "Stats" },
];

export function ScamKgView() {
  const { me } = useSession();
  // The axios interceptor's org header and this view must target the same org,
  // so the selected org wins over the session's home org.
  const orgId = useMemo(() => org.selected() || me?.organization_id || "", [me?.organization_id]);
  const [tab, setTab] = useState<KgTab>("explorer");

  const stats = useAsync<ScamKgStats>(
    () =>
      orgId
        ? http.get<ScamKgStats>(`/scam-kg/stats${qs({ organization_id: orgId })}`)
        : Promise.resolve<ScamKgStats>({}),
    [orgId]
  );

  const statRows = useMemo(
    () => Object.entries(stats.data?.counts_by_type ?? {}).sort((a, b) => b[1] - a[1]),
    [stats.data]
  );

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Scam knowledge graph</h1>
          <div className="sub">
            Grounded retrieval over the provenance-preserving KG — the graph reasons, the model narrates.
          </div>
        </div>
        <div className="page-actions">
          <button className="btn" onClick={() => stats.reload()} disabled={stats.loading || !orgId}>
            {stats.loading ? <Spinner /> : "↻"} Refresh stats
          </button>
        </div>
      </div>

      {!orgId && (
        <Banner tone="warning">
          No organisation is selected, so the graph service has nothing to scope a query to. Pick an
          organisation to load the knowledge graph.
        </Banner>
      )}

      <Tabs tabs={KG_TABS} active={tab} onChange={(id) => setTab(id as KgTab)} />

      {tab === "retrieve" && <KgRetrievePanel orgId={orgId} />}

      {tab === "stats" && (
        <div className="stack">
          <ErrorBanner error={stats.error} what="Could not load graph statistics" />
          <div className="grid c3">
            <StatTile label="Nodes" value={asNumber(stats.data?.node_count)} />
            <StatTile label="Classifications" value={statRows.length} />
            <StatTile label="Queue pending" value={asNumber(stats.data?.queue_pending)} />
          </div>
          {statRows.length > 0 && (
            <BarChartCard
              title="Nodes by classification"
              horizontal
              yLabel="nodes"
              note="Top 15 node types in this organisation's graph."
              data={statRows.slice(0, 15).map(([label, value]) => ({ label, value }))}
            />
          )}
          <div className="card flush">
            <div className="card-head">
              <h3>Node types</h3>
            </div>
            <div className="card-body">
              <Table
                columns={[
                  { key: "type", header: "Node type", sortable: true, render: (r) => <span className="mono">{r.type}</span> },
                  { key: "count", header: "Count", align: "right", sortable: true, render: (r) => <Num value={r.count} decimals={0} /> },
                ]}
                rows={statRows.map(([type, count]) => ({ id: type, type, count }))}
                loading={stats.loading}
                empty="No nodes have been ingested yet"
              />
            </div>
          </div>
        </div>
      )}

      {tab === "explorer" && <KgExplorerPanel orgId={orgId} total={stats.data?.node_count} />}
    </div>
  );
}

// ---- retrieve panel --------------------------------------------------------

function KgRetrievePanel({ orgId }: { orgId: string }) {
  const [text, setText] = useState("Scan this QR to receive your refund and share the OTP");
  const [channel, setChannel] = useState("whatsapp");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<ScamKgRetrieve | null>(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await http.post<ScamKgRetrieve>("/scam-kg/retrieve", {
        organization_id: orgId,
        text,
        channel,
        now: new Date().toISOString(),
      });
      setResult(data);
    } catch (err) {
      setError(err);
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const playbook = result?.playbook;
  const provenance = result?.provenance ?? [];

  return (
    <div className="grid c2">
      <div className="card">
        <div className="card-head">
          <h3>Grounded retrieval</h3>
        </div>
        <div className="card-body">
          <Field label="Suspicious message">
            <textarea rows={4} value={text} placeholder="Paste a suspicious message…" onChange={(e) => setText(e.target.value)} />
          </Field>
          <div className="row">
            <select value={channel} aria-label="Channel" style={{ width: 160 }} onChange={(e) => setChannel(e.target.value)}>
              <option value="whatsapp">WhatsApp</option>
              <option value="sms">SMS</option>
              <option value="voice">Voice</option>
              <option value="email">Email</option>
            </select>
            <button className="btn primary" disabled={loading || !orgId || !text.trim()} onClick={() => void run()}>
              {loading && <Spinner />} {loading ? "Retrieving…" : "Retrieve"}
            </button>
          </div>

          <div style={{ marginTop: 12 }}>
            <ErrorBanner error={error} what="Retrieval failed" />
          </div>

          {result && (
            <div className="muted" style={{ fontSize: 12 }}>
              routing: {result.routing?.mode ?? "—"} · dense: {String(result.routing?.need_dense ?? "—")} · backend:{" "}
              {result.backend ?? "—"}
            </div>
          )}
        </div>
      </div>

      <div className="stack">
        {loading && (
          <div className="card">
            <div className="row">
              <Spinner />
              <span className="muted">Running grounded retrieval…</span>
            </div>
          </div>
        )}

        {!loading && !result && !error && (
          <div className="card">
            <Empty
              title="No result yet"
              body="Paste a message and press Retrieve to see the verdict, the SPRT ledger and the SOP."
              icon="⌕"
            />
          </div>
        )}

        {result && <VerdictLedger verdict={result.verdict ?? {}} trail={result.evidence_trail ?? []} />}

        {playbook?.steps && playbook.steps.length > 0 && (
          <div className="card">
            <div className="card-head">
              <h3>
                SOP · {playbook.mechanism ?? "—"} <span className="muted">({playbook.urgency_tier ?? "—"})</span>
              </h3>
            </div>
            <div className="card-body">
              <ol style={{ margin: 0, paddingLeft: 18 }}>
                {playbook.steps.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ol>
              <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
                Helpline {playbook.official_rails?.helpline ?? "—"} · {playbook.official_rails?.portal ?? "—"}
              </div>
            </div>
          </div>
        )}

        {provenance.length > 0 && (
          <div className="card">
            <div className="card-head">
              <h3>Provenance</h3>
            </div>
            <div className="card-body">
              <ul className="mono" style={{ margin: 0, paddingLeft: 18 }}>
                {provenance.map((p, i) => (
                  <li key={i} className="muted">
                    {p.source_id ?? "—"} · {(p.raw_key ?? "").slice(0, 12)}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The role-typed verdict and its SPRT ledger. Λ is shown as a position between
 * the two Wald boundaries — B accepts "legit", A declares "scam" — because the
 * absolute value means nothing without them.
 */
function VerdictLedger({ verdict, trail }: { verdict: ScamKgVerdict; trail: ScamKgEvidence[] }) {
  const A = verdict.A ?? 3;
  const B = verdict.B ?? -3;
  const lambda = verdict.Lambda ?? 0;
  const pct = A === B ? 50 : Math.max(0, Math.min(100, ((lambda - B) / (A - B)) * 100));
  const band = verdict.band || "low_signal";
  const tone =
    band === "high_risk" ? "critical" : band === "caution" ? "warning" : band === "needs_review" ? "info" : "good";

  return (
    <div className="card">
      <div className="card-head">
        <h3>Verdict {verdict.role && <span className="muted">· {verdict.role}</span>}</h3>
        <Badge tone={tone}>{band}</Badge>
      </div>
      <div className="card-body">
        <div className="muted" style={{ fontSize: 12 }}>
          label <span className="mono">{verdict.label || "collecting"}</span> · evidence {asNumber(verdict.n_evidence)}
        </div>

        <div style={{ marginTop: 12 }}>
          <div className="row muted" style={{ fontSize: 11 }}>
            <span>B={B.toFixed(2)} (legit)</span>
            <span className="spacer" />
            <span>Λ={lambda.toFixed(2)}</span>
            <span className="spacer" />
            <span>A={A.toFixed(2)} (scam)</span>
          </div>
          <div className="meter" style={{ marginTop: 4 }} role="img" aria-label={`Lambda ${lambda.toFixed(2)} between ${B.toFixed(2)} and ${A.toFixed(2)}`}>
            <span style={{ width: `${pct}%` }} />
          </div>
        </div>

        {trail.length > 0 && (
          <div className="section" style={{ marginTop: 16 }}>
            <h4>Evidence ledger (append-only)</h4>
            <div className="col">
              {trail.map((e, i) => (
                <div className="row" key={i}>
                  <span className="mono truncate">{e.evidence_type || e.discriminator || "—"}</span>
                  <span className="spacer" />
                  {e.llr != null && <span className="muted num">llr {asNumber(e.llr).toFixed(2)}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---- explorer panel --------------------------------------------------------

function KgExplorerPanel({ orgId, total }: { orgId: string; total?: number }) {
  const mode = useMode();
  const toast = useToast();
  const canvas = useRef<GraphHandle>(null);

  const [seedKind, setSeedKind] = useState("");
  const [limit, setLimit] = useState(1000);
  const [colorBy, setColorBy] = useState<ColorBy>("type");
  const [strataFilter, setStrataFilter] = useState("");

  const [nodes, setNodes] = useState<KgNode[]>([]);
  const [edges, setEdges] = useState<KgEdge[]>([]);
  const [truncated, setTruncated] = useState(0);
  const expanded = useRef(new Set<string>()).current;

  const [selected, setSelected] = useState<KgNode | null>(null);
  // Read by the in-flight detail fetch to decide whether its answer is still
  // wanted. A ref, not the state value, because the fetch outlives the render
  // that started it.
  const selectedIdRef = useRef<string | null>(null);
  const [detail, setDetail] = useState<KgNodeDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [raw, setRaw] = useState<KgNodeRaw | null>(null);
  const [rawLoading, setRawLoading] = useState(false);

  const seed = useAsync<KgOverview>(
    () =>
      orgId
        ? http.get<KgOverview>(
            `/scam-kg/graph/overview${qs({ organization_id: orgId, kind: seedKind, limit })}`
          )
        : Promise.resolve<KgOverview>({}),
    [orgId, seedKind, limit]
  );

  useEffect(() => {
    if (!seed.data) return;
    expanded.clear();
    setTruncated(0);
    selectedIdRef.current = null;
    setSelected(null);
    setDetail(null);
    setRaw(null);
    setNodes(seed.data.nodes ?? []);
    setEdges(seed.data.edges ?? []);
  }, [seed.data, expanded]);

  const expandNode = useCallback(
    async (node: KgNode) => {
      if (expanded.has(node.id)) return;
      expanded.add(node.id);
      try {
        const data = await http.get<KgNeighbors>(
          `/scam-kg/graph/nodes/${node.id}/neighbors${qs({ organization_id: orgId })}`
        );
        const all = data.neighbors ?? [];
        const keep = all.slice(0, NEIGHBOR_CAP);
        if (all.length > NEIGHBOR_CAP) setTruncated((t) => t + (all.length - NEIGHBOR_CAP));
        setNodes((prev) => {
          const byId = new Map(prev.map((n) => [n.id, n]));
          for (const n of keep) if (!byId.has(n.id)) byId.set(n.id, n);
          return Array.from(byId.values());
        });
        setEdges((prev) => [...prev, ...(data.edges ?? [])]);
      } catch (err) {
        toast.push(errMessage(err, "Failed to expand this node"), "critical");
      }
    },
    [expanded, orgId, toast]
  );

  const loadDetail = useCallback(
    async (id: string) => {
      setDetailLoading(true);
      try {
        const data = await http.get<KgNodeDetail>(
          `/scam-kg/graph/nodes/${id}${qs({ organization_id: orgId })}`
        );
        // Discard a response whose node is no longer the selection.
        if (selectedIdRef.current === id) setDetail(data);
      } catch (err) {
        toast.push(errMessage(err, "Failed to load node detail"), "critical");
      } finally {
        setDetailLoading(false);
      }
    },
    [orgId, toast]
  );

  const loadRaw = async () => {
    if (!selected) return;
    setRawLoading(true);
    try {
      const data = await http.get<KgNodeRaw>(
        `/scam-kg/graph/nodes/${selected.id}/raw${qs({ organization_id: orgId })}`
      );
      setRaw(data);
    } catch {
      setRaw({ has_raw: false, reason: "failed to load raw payload" });
    } finally {
      setRawLoading(false);
    }
  };

  const onNodeClick = (id: string) => {
    const node = nodes.find((n) => n.id === id);
    if (!node) return;
    selectedIdRef.current = id;
    setSelected(node);
    setDetail(null);
    setRaw(null);
    void loadDetail(id);
    void expandNode(node);
  };

  // The strata filter REMOVES nodes rather than dimming them, so the force
  // layout re-settles around the isolated segment instead of leaving a hole.
  const visibleNodes = useMemo(
    () => (strataFilter ? nodes.filter((n) => String(n.strata ?? "") === strataFilter) : nodes),
    [nodes, strataFilter]
  );

  const strataOptions = useMemo(() => {
    const s = new Set<string>();
    for (const n of nodes) if (n.strata) s.add(String(n.strata));
    return Array.from(s).sort();
  }, [nodes]);

  useEffect(() => {
    if (strataFilter && !strataOptions.includes(strataFilter)) setStrataFilter("");
  }, [strataOptions, strataFilter]);

  const vizNodes = useMemo<VizNode[]>(
    () =>
      visibleNodes.map((n) => {
        const value = axisValue(n, colorBy);
        return {
          id: n.id,
          label: humanName(n),
          typeLabel: humanType(n.node_type),
          domain: kgDomain(n.node_type),
          radius: Math.max(3, kgSize(n.node_type) * 0.9),
          pinLabel: kgSize(n.node_type) >= 5,
          tint:
            colorBy === "type"
              ? undefined
              : value
              ? seriesColor(value, mode)
              : NODE_DOMAIN_COLOR.unknown,
        };
      }),
    [visibleNodes, colorBy, mode]
  );

  const vizLinks = useMemo<VizLink[]>(() => {
    const present = new Set(visibleNodes.map((n) => n.id));
    const out: VizLink[] = [];
    const seen = new Set<string>();
    for (const e of edges) {
      const s = e.from_node_id ?? "";
      const t = e.to_node_id ?? "";
      if (!present.has(s) || !present.has(t)) continue;
      const key = `${s}|${e.type ?? ""}|${t}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ id: key, source: s, target: t, rel: e.type ?? "" });
    }
    return out;
  }, [edges, visibleNodes]);

  const shownDomains = useMemo(() => {
    const present = new Set(vizNodes.map((n) => n.domain));
    return DOMAINS.filter((d) => present.has(d));
  }, [vizNodes]);

  // Legend for the active non-type axis, rebuilt from the values actually present.
  const axisLegend = useMemo(() => {
    if (colorBy === "type") return null;
    const counts = new Map<string, number>();
    let unknown = 0;
    for (const n of visibleNodes) {
      const v = axisValue(n, colorBy);
      if (v == null) unknown++;
      else counts.set(v, (counts.get(v) ?? 0) + 1);
    }
    const CAP = 14;
    const sorted = Array.from(counts.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    return {
      shown: sorted.slice(0, CAP).map(([value, count]) => ({
        value,
        count,
        color: seriesColor(value, mode),
        text: axisLabel(value, colorBy),
      })),
      more: Math.max(0, sorted.length - CAP),
      unknown,
    };
  }, [colorBy, visibleNodes, mode]);

  const signals = useMemo<string[]>(() => {
    const terms = detail?.sparse_space?.terms ?? [];
    const out: string[] = [];
    for (const t of terms) {
      if (KG_TERM_LABELS[t] || t.includes(":") || /signal|risk/i.test(t)) out.push(humanTerm(t));
      if (out.length >= 20) break;
    }
    return Array.from(new Set(out)).slice(0, 12);
  }, [detail]);

  const segmentRows = useMemo<Array<[string, string]>>(() => {
    const p = detail?.properties ?? {};
    const pairs: Array<[string, unknown]> = [
      ["User strata", selected?.user_strata ?? p.user_strata_code],
      ["User cluster", selected?.user_cluster ?? p.user_cluster_code],
      ["Scam awareness", p.scam_awareness],
      ["Emotional state", selected?.emotional_state ?? p.emotional_state ?? p.state_code],
      ["Mirroring layer", selected?.mirror_role],
      ["Preferred style", p.style_code],
      ["Digital literacy", p.digital_literacy_band],
      ["Behavioural trait", p.behavioral_trait],
      ["Awareness prior", p.scam_awareness_prior],
    ];
    return pairs.filter(([, v]) => v != null && v !== "").map(([k, v]) => [k, String(v)]);
  }, [detail, selected]);

  const groundingRows = useMemo<Array<[string, string]>>(() => {
    const p = detail?.properties ?? {};
    const pairs: Array<[string, unknown]> = [
      ["Daily life", p.daily_life],
      ["Speech style", p.speech_style],
      ["Self-presentation", p.self_presentation],
      ["Contextual exposure", p.contextual_exposure],
    ];
    return pairs.filter(([, v]) => v != null && v !== "").map(([k, v]) => [k, String(v)]);
  }, [detail]);

  const propRows = useMemo(() => Object.entries(detail?.properties ?? {}), [detail]);

  return (
    <div className="stack">
      <div className="toolbar">
        <select value={seedKind} aria-label="Seed node type" style={{ width: 200 }} onChange={(e) => setSeedKind(e.target.value)}>
          {KG_SEED_KINDS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select value={limit} aria-label="Node limit" style={{ width: 170 }} onChange={(e) => setLimit(Number(e.target.value))}>
          {KG_LIMITS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select value={colorBy} aria-label="Colour by" style={{ width: 180 }} onChange={(e) => setColorBy(e.target.value as ColorBy)}>
          {COLOR_BY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              Colour by: {o.label}
            </option>
          ))}
        </select>
        {strataOptions.length > 0 && (
          <select value={strataFilter} aria-label="Filter to one strata" style={{ width: 170 }} onChange={(e) => setStrataFilter(e.target.value)}>
            <option value="">All strata</option>
            {strataOptions.map((s) => (
              <option key={s} value={s}>
                {titleCase(s)}
              </option>
            ))}
          </select>
        )}
        <button className="btn" onClick={() => seed.reload()} disabled={seed.loading || !orgId}>
          {seed.loading ? <Spinner /> : "↻"} Reload
        </button>
        <button className="btn" onClick={() => canvas.current?.fit()}>
          Fit view
        </button>
      </div>

      <ErrorBanner error={seed.error} what="Could not load the knowledge graph" />

      <GraphCanvas
        ref={canvas}
        nodes={vizNodes}
        links={vizLinks}
        selectedId={selected?.id ?? null}
        height={620}
        onNodeClick={onNodeClick}
        onBackgroundClick={() => {
          selectedIdRef.current = null;
          setSelected(null);
        }}
      >
        <div className="graph-controls">
          <span className="tag">
            {vizNodes.length} nodes · {vizLinks.length} links
            {total ? ` · of ${total} total` : ""}
            {truncated ? ` · ${truncated} more hidden` : ""}
          </span>
        </div>

        {seed.loading && (
          <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", zIndex: 3 }}>
            <div className="row">
              <Spinner />
              <span className="muted">Loading the knowledge graph…</span>
            </div>
          </div>
        )}
        {!seed.loading && vizNodes.length === 0 && (
          <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", zIndex: 3 }}>
            <Empty
              title="Nothing to explore yet"
              body={
                orgId
                  ? "No nodes came back for this seed type. Widen the seed or clear the strata filter."
                  : "Select an organisation to load its knowledge graph."
              }
              icon="◌"
            />
          </div>
        )}

        {selected && (
          <div className="graph-detail">
            <div className="row">
              <ShapeSwatch domain={kgDomain(selected.node_type)} title={humanType(selected.node_type)} />
              <span className="muted truncate">{humanType(selected.node_type)}</span>
              <button
                className="btn icon sm ghost"
                onClick={() => {
                  selectedIdRef.current = null;
                  setSelected(null);
                }}
                aria-label="Close node detail"
              >
                ✕
              </button>
            </div>
            <h3 style={{ marginTop: 4 }}>{humanName(selected)}</h3>
            <div className="muted" style={{ fontSize: 12 }}>
              {KG_TYPE_DESCRIPTIONS[selected.node_type] ??
                `A ${humanType(selected.node_type).toLowerCase()} in the graph.`}
            </div>

            {selected.dataset_label && (
              <div className="section">
                <h4>Dataset label</h4>
                <div className="chips">
                  <Badge tone="warning">{titleCase(selected.dataset_label)}</Badge>
                </div>
                <div className="muted" style={{ fontSize: 11 }}>
                  Source corpus label — weak evidence, not the verdict.
                </div>
              </div>
            )}

            <div className="section">
              <h4>Working verdict</h4>
              <dl className="kv">
                <dt>Verdict</dt>
                <dd title="Starts neutral; only becomes 'scam' once evidence crosses the bar.">
                  {verdictText(selected.current_label)}
                </dd>
                <dt>Risk score</dt>
                <dd>{selected.risk_score != null ? asNumber(selected.risk_score).toFixed(2) : "—"}</dd>
                <dt>Status</dt>
                <dd>{titleCase(selected.review_status || "—")}</dd>
                <dt>Trust</dt>
                <dd>{selected.trust_weight != null ? asNumber(selected.trust_weight).toFixed(2) : "—"}</dd>
              </dl>
              {L1_TYPES.has(selected.node_type) && (
                <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                  Raw facts start neutral and are never overwritten; the verdict is derived from evidence.
                </div>
              )}
            </div>

            {detailLoading && (
              <div className="row">
                <Spinner />
                <span className="muted">Loading detail…</span>
              </div>
            )}

            {detail && (detail.detector || detail.routing_mode || detail.top_hub || detail.llr_contribution != null) && (
              <div className="section">
                <h4>Detector</h4>
                <dl className="kv">
                  {detail.detector && (
                    <>
                      <dt>Detector</dt>
                      <dd>{titleCase(detail.detector)}</dd>
                    </>
                  )}
                  {detail.routing_mode && (
                    <>
                      <dt>Routing</dt>
                      <dd>{detail.routing_mode}</dd>
                    </>
                  )}
                  {detail.top_hub && (
                    <>
                      <dt>Top hub</dt>
                      <dd>{detail.top_hub}</dd>
                    </>
                  )}
                  {detail.llr_contribution != null && (
                    <>
                      <dt>LLR</dt>
                      <dd>{asNumber(detail.llr_contribution).toFixed(2)}</dd>
                    </>
                  )}
                </dl>
              </div>
            )}

            {signals.length > 0 && (
              <div className="section">
                <h4>Signals detected</h4>
                <div className="chips">
                  {signals.map((s, i) => (
                    <span className="tag" key={i}>
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {segmentRows.length > 0 && (
              <div className="section">
                <h4>Segment and mirroring</h4>
                <dl className="kv">
                  {segmentRows.map(([k, v]) => (
                    <div key={k} style={{ display: "contents" }}>
                      <dt>{k}</dt>
                      <dd>{titleCase(v)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            {groundingRows.length > 0 && (
              <div className="section">
                <h4>Cultural grounding</h4>
                <dl className="kv">
                  {groundingRows.map(([k, v]) => (
                    <div key={k} style={{ display: "contents" }}>
                      <dt>{k}</dt>
                      <dd>{v}</dd>
                    </div>
                  ))}
                </dl>
                <div className="muted" style={{ fontSize: 11 }}>
                  Grounded from data to mirror respectfully — never a guess about an individual.
                </div>
              </div>
            )}

            <button className="btn block" style={{ marginTop: 8 }} onClick={() => setDrawerOpen(true)}>
              Full detail
            </button>
          </div>
        )}
      </GraphCanvas>

      {colorBy === "type" ? (
        <DomainLegend shown={shownDomains.length > 0 ? shownDomains : [...DOMAINS]} />
      ) : (
        <div className="legend">
          <span className="item strong">{COLOR_BY_OPTIONS.find((o) => o.value === colorBy)?.label}</span>
          {axisLegend?.shown.map((e) => (
            <span className="item" key={e.value} title={`${e.value} · ${e.count} node(s)`}>
              <span className="swatch" style={{ background: e.color }} />
              {e.text}
            </span>
          ))}
          {axisLegend && axisLegend.more > 0 && <span className="item muted">+{axisLegend.more} more</span>}
          {axisLegend && axisLegend.unknown > 0 && (
            <span className="item">
              <span className="swatch" style={{ background: NODE_DOMAIN_COLOR.unknown }} />
              Unknown ({axisLegend.unknown})
            </span>
          )}
          {axisLegend && axisLegend.shown.length === 0 && axisLegend.unknown === 0 && (
            <span className="item muted">no data for this axis yet</span>
          )}
        </div>
      )}

      <div className="row">
        <span className="muted">Node shape and label carry the type; colour repeats it.</span>
        <span className="spacer" />
        <span className="muted">scroll to zoom · drag to pan · hover to focus · click a node</span>
      </div>

      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title={selected ? humanName(selected) : "Node"}>
        {selected && (
          <div className="stack">
            <dl className="kv">
              <dt>Type</dt>
              <dd>
                {humanType(selected.node_type)} <span className="mono muted">({selected.node_type})</span>
              </dd>
              <dt>Id</dt>
              <dd className="mono">{selected.id}</dd>
              {detail?.event_time && (
                <>
                  <dt>Event time</dt>
                  <dd>{detail.event_time}</dd>
                </>
              )}
              {detail?.record_time && (
                <>
                  <dt>Recorded</dt>
                  <dd>{detail.record_time}</dd>
                </>
              )}
            </dl>

            <div className="section">
              <h4>Raw data (JSON)</h4>
              <button className="btn sm" onClick={() => (raw ? setRaw(null) : void loadRaw())} disabled={rawLoading}>
                {rawLoading && <Spinner />} {raw ? "Hide" : "Load raw payload"}
              </button>
              {raw && !raw.has_raw && (
                <div className="muted" style={{ marginTop: 8 }}>
                  {raw.reason || "No raw payload (derived node)."}
                </div>
              )}
              {raw?.has_raw && (
                <>
                  <pre className="pre-block">
                    {(() => {
                      const payload = raw.payload_json ?? raw.payload_text;
                      try {
                        return JSON.stringify(payload, null, 2);
                      } catch {
                        return String(payload);
                      }
                    })()}
                  </pre>
                  <div className="muted" style={{ fontSize: 11 }}>
                    {asNumber(raw.size_bytes)} bytes · {raw.content_type ?? "unknown"}
                    {raw.truncated ? " · truncated" : ""}
                  </div>
                </>
              )}
            </div>

            <div className="section">
              <h4>All properties ({propRows.length})</h4>
              {propRows.length === 0 ? (
                <div className="muted">No stored properties.</div>
              ) : (
                <dl className="kv">
                  {propRows.map(([k, v]) => (
                    <div key={k} style={{ display: "contents" }}>
                      <dt className="mono">{k}</dt>
                      <dd>{v === null ? "—" : String(v)}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>

            <div className="section">
              <h4>Knowledge representation (three spaces)</h4>
              <div className="muted" style={{ fontSize: 12 }}>
                Sparse — exact features ({asNumber(detail?.sparse_space?.count)})
              </div>
              <div className="chips" style={{ marginTop: 4 }}>
                {(detail?.sparse_space?.terms ?? []).slice(0, 40).map((t, i) => (
                  <span className="tag mono" key={i}>
                    {t}
                  </span>
                ))}
                {(detail?.sparse_space?.terms ?? []).length === 0 && <span className="muted">none</span>}
              </div>

              <hr className="sep" />
              <div className="muted" style={{ fontSize: 12 }}>
                Shallow-dense — summary vector
              </div>
              <div className="mono muted" style={{ fontSize: 11 }}>
                {asNumber(detail?.shallow_dense_space?.dim)}-dim
                {(detail?.shallow_dense_space?.vector_preview ?? []).length > 0 &&
                  ` · [${(detail?.shallow_dense_space?.vector_preview ?? []).slice(0, 6).join(", ")} …]`}
              </div>

              <hr className="sep" />
              <div className="muted" style={{ fontSize: 12 }}>
                Deep-dense — raw payload:{" "}
                {detail?.deep_dense_space?.stored ? "kept in blob store (audit only)" : "derived node — no raw payload"}
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
